import axiosSetup from "../utils/axiosSetup";
import toast from "react-hot-toast";
import {
  IChange,
  IRepetitiveTaskManager,
  ITaskManagement,
} from "../models/taskManagement.model";
import { ITaskNotification } from "../models/taskNotifications.model";

export const createTaskManager = async (
  taskData: FormData | Partial<ITaskManagement>
) => {
  try {
    const config =
      taskData instanceof FormData
        ? {
            headers: { "Content-Type": "multipart/form-data" },
          }
        : undefined;

    const response = await axiosSetup.post(
      "/taskManager/add-task",
      taskData,
      config
    );

    return response?.data?.data;
  } catch (error) {
    console.error("Failed to create task:", error);
    toast.error("Failed to create task");
    throw error;
  }
};

export const fetchTaskManager = async (
  status: string,
  assignee?: string,
  follower?: string,
  author?: string,
  startDate?: string,
  endDate?: string,
  page?: number,
  limit?: number
) => {
  try {
    const response = await axiosSetup.get("/taskManager/get-task", {
      params: {
        ...(status ? { status } : {}),
        ...(page ? { page } : { page: 1 }),
        ...(limit ? { limit } : { limit: 10 }),
        ...(follower ? { follower } : {}),
        ...(author ? { author } : {}),
        ...(assignee ? { assignee } : {}),
        ...(startDate ? { startDate } : {}),
        ...(endDate ? { endDate } : {}),
      },
    });
    return response?.data?.data as {
      tasks: ITaskManagement[];
    };
  } catch (error) {
    console.error("Failed to fetch tasks:", error);
    toast.error("Failed to fetch tasks");
    throw error;
  }
};

export const fetchRepetitiveTaskManager = async (
  status: string,
  assignee?: string,
  follower?: string,
  startDate?: string,
  endDate?: string,
  page?: number,
  limit?: number
) => {
  try {
    const response = await axiosSetup.get(
      "/taskManager/get-repetitive-task-manager",
      {
        params: {
          ...(status ? { status } : { status: "ongoing" }),
          ...(page ? { page } : { page: 1 }),
          ...(limit ? { limit } : { limit: 10 }),
          ...(follower ? { follower } : {}),
          ...(assignee ? { assignee } : {}),
          ...(startDate ? { startDate } : {}),
          ...(endDate ? { endDate } : {}),
        },
      }
    );
    return response?.data?.data as {
      repetitiveTasks: IRepetitiveTaskManager[];
    };
  } catch (error) {
    console.error("Failed to fetch tasks:", error);
    toast.error("Failed to fetch tasks");
    throw error;
  }
};

export const updateTaskManagerApi = async (
  id: string,
  taskData: Partial<ITaskManagement>,
  changes?: {
    field: string;
    oldValue: ITaskManagement;
    newValue: ITaskManagement;
  }[]
) => {
  try {
    const response = await axiosSetup.put(
      `/taskManager/update-task-manager/${id}`,
      { taskData, changes }
    );
    return response?.data?.data;
  } catch (error) {
    console.error("Failed to update task:", error);
    toast.error("Failed to update task");
    throw error;
  }
};

export const updateRepetitiveTaskManagerApi = async (
  id: string,
  taskData: IRepetitiveTaskManager,
  changes?: {
    field: string;
    oldValue: IRepetitiveTaskManager;
    newValue: IRepetitiveTaskManager;
  }[]
) => {
  try {
    const response = await axiosSetup.put(
      `/taskManager/update-repetitive-task-manager/${id}`,
      { taskData, changes }
    );
    return response?.data?.data;
  } catch (error) {
    console.error("Failed to update task:", error);
    toast.error("Failed to update task");
    throw error;
  }
};

export const updateTaskManagerAssignees = async (data: {
  taskIds: string[];
  assignee: string;
  change: IChange;
}) => {
  try {
    const response = await axiosSetup.put(
      "/taskManager/update-task-manager-assignees",
      data
    );
    return response?.data?.data;
  } catch (error) {
    console.error("Failed to update assignees:", error);
    toast.error("Failed to update assignees");
    throw error;
  }
};

export const updateTaskStatus = async (
  taskId: string,
  approxTime?: string,
  decision?: "approved" | "rejected"
) => {
  try {
    const resp = await axiosSetup.put(
      `/taskManager/update-task-manager-status/${taskId}`,
      { approxTime, decision }
    );
    return resp.data.data as ITaskManagement;
  } catch (err) {
    console.error("Failed to update task status:", err);
    toast.error("Unable to mark complete");
    throw err;
  }
};

export const fetchTaskMetaData = async () => {
  try {
    const resp = await axiosSetup.get("/taskManager/get-task-metadata");
    return resp.data.data[0];
  } catch (err) {
    console.error("Failed to fetch metadata:", err);
    toast.error("Unable to load");
    throw err;
  }
};

export const updateDuplicateTask = async (
  currentTaskId: string,
  duplicateOfTaskId: string,
  remark: string
) => {
  try {
    const resp = await axiosSetup.put("/taskManager/update-task-as-duplicate", {
      currentTaskId,
      duplicateOfTaskId,
      remark,
    });
    return resp.data.data;
  } catch (err) {
    console.error("Failed to mark duplicate:", err);
    toast.error("Failed to mark duplicate");
    throw err;
  }
};

export const fetchTaskManagerLogs = async (taskId: string) => {
  try {
    const response = await axiosSetup.get(
      `/taskManager/get-task-manager-logs/${taskId}`
    );
    return response?.data?.data;
  } catch (error) {
    console.error("Failed to fetch task logs:", error);
    throw error;
  }
};

export const fetchRepetitiveTaskManagerLogs = async (taskId: string) => {
  try {
    const response = await axiosSetup.get(
      `/taskManager/get-repetitive-task-manager-logs/${taskId}`
    );
    return response?.data?.data;
  } catch (error) {
    console.error("Failed to fetch task logs:", error);
    throw error;
  }
};

export const fetchOnetaskById = async (taskId: string) => {
  try {
    const response = await axiosSetup.get(
      `/taskManager/get-one-task-manager/${taskId}`
    );
    return response?.data?.data;
  } catch (error) {
    console.error("Failed to fetch task:", error);
    throw error;
  }
};

export const fetchOneRepetitiveTask = async (taskId: string) => {
  try {
    const response = await axiosSetup.get(
      `/taskManager/get-one-repetitive-task-manager/${taskId}`
    );
    return response?.data?.data;
  } catch (error) {
    console.error("Failed to fetch task:", error);
    throw error;
  }
};

export const updateTaskAttachmentsApi = async (
  taskId: string,
  payload: FormData | { removeAttachment: string[] }
) => {
  try {
    const response = await axiosSetup.put(
      `/taskManager/update-task-attachments/${taskId}`,
      payload
    );
    return response?.data?.data;
  } catch (error) {
    console.error("Failed to update attachments:", error);
    toast.error("Failed to update attachments");
    throw error;
  }
};

export const updateRepetitiveTaskAttachmentsApi = async (
  taskId: string,
  payload: FormData | { removeAttachment: string[] }
) => {
  try {
    const response = await axiosSetup.put(
      `/taskManager/update-repetitive-task-attachments/${taskId}`,
      payload
    );
    return response?.data?.data;
  } catch (error) {
    console.error("Failed to update attachments:", error);
    toast.error("Failed to update attachments");
    throw error;
  }
};

export const updateTaskManagerCommentApi = async (
  taskId: string,
  mentions: string[],
  comment: string
) => {
  try {
    const response = await axiosSetup.put(
      `/taskManager/update-task-manager-comment/${taskId}`,
      { mentions, comment }
    );
    return response?.data?.data;
  } catch (error) {
    console.error("Failed to add comment:", error);
    toast.error("Failed to add comment");
    throw error;
  }
};

export const updateTaskRequestedEndDateApi = async (
  taskId: string,
  requestedEndDate?: Date,
  decision?: "approved" | "rejected"
) => {
  try {
    const response = await axiosSetup.put(
      `/taskManager/update-task-requested-end-date/${taskId}`,
      { requestedEndDate: requestedEndDate?.toISOString(), decision }
    );
    return response?.data?.data;
  } catch (error) {
    console.error("Failed to update requested end date:", error);
    toast.error("Failed to extend deadline");
    throw error;
  }
};

export const fetchTaskNotificationCount = async () => {
  const response = await axiosSetup.get(
    `taskManager/get-task-notification-count`
  );
  return response.data.data;
};

export const fetchAuthorApprovalTasks = async () => {
  try {
    const resp = await axiosSetup.get("/taskManager/get-author-approval-tasks");
    return resp?.data?.data?.tasks ?? [];
  } catch (err) {
    console.error("Failed to fetch author approval tasks:", err);
    throw err;
  }
};

export const fetchTaskManagerNotifications = async (
  page: number = 1,
  limit: number = 10
): Promise<{ notifications: ITaskNotification[]; pagination: any }> => {
  try {
    const resp = await axiosSetup.get(
      "/taskManager/get-task-manager-notifications",
      {
        params: { page, limit },
      }
    );
    return resp.data.data;
  } catch (err) {
    console.error("Failed to fetch notifications:", err);
    toast.error("Failed to fetch notifications");
    throw err;
  }
};

export const fetchTaskManagerUnreadNotificationCount = async (
  isRead = false
) => {
  try {
    const response = await axiosSetup.get(
      "/taskManager/get-task-manager-notifications-unread-count",
      {
        params: { isRead },
      }
    );

    return response.data.data;
  } catch (error) {
    console.error("Failed to fetch unread notification count:", error);
    throw error;
  }
};

export const updateStopRepetitiveTaskApi = async (taskId: string) => {
  try {
    const response = await axiosSetup.put(
      "/taskManager/update-stop-repetitive-task",
      { taskId, isCompleted: true }
    );
    return response?.data?.data;
  } catch (error) {
    console.error("Failed to stop repetitive task:", error);
    toast.error("Failed to stop repetitive task");
    throw error;
  }
};
